import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import clsx from 'clsx';
import { IconAlert, IconCheckMark, IconError, IconInfo, IconClose, } from '../../internal/icons';
import { getToastComponentStyle, toastClasses } from './style';
import { ActionButton } from '../../Shared/ActionButton';
import { Button } from '../../Button';
import { useTranslation } from '../../hooks/useTranslation';
const defaultProps = {
    type: 'info',
};
function getIcon(props) {
    const { type, icon } = props;
    let IconComp = () => (React.createElement(IconInfo, { className: clsx(toastClasses.icon, toastClasses.iconInfo) }));
    if (icon) {
        // eslint-disable-next-line react/jsx-no-useless-fragment
        IconComp = () => React.createElement(React.Fragment, null, icon);
    }
    else if (type === 'success') {
        IconComp = () => (React.createElement(IconCheckMark, { className: clsx(toastClasses.icon, toastClasses.iconSuccess) }));
    }
    else if (type === 'warn' || type === 'warning') {
        IconComp = () => (React.createElement(IconAlert, { className: clsx(toastClasses.icon, toastClasses.iconWarn) }));
    }
    else if (type === 'error') {
        IconComp = () => (React.createElement(IconError, { className: clsx(toastClasses.icon, toastClasses.iconError) }));
    }
    return React.createElement(IconComp, null);
}
function parseSizeValue(size) {
    if (typeof size === 'number') {
        return `${size}px`;
    }
    if (typeof size === 'string' && /^[0-9.]{1,}%$/.test(size)) {
        const sizeNum = (parseFloat(size.replace(/%$/, '')) / 100) * window.innerWidth;
        return `${sizeNum}px`;
    }
    return size;
}
export const ToastComponent = (props) => {
    const ref = useRef(null);
    const { t } = useTranslation('Shared');
    const resultProps = { ...defaultProps, ...props };
    const { className, toastKey, title, type, content, closable, actionable, afterClose, onClickCloseButton, cancelText = t('cancel'), okText = t('ok'), onCancel, onOk, reverseButtons, closeButtonProps, titleProps, contentProps, okButtonProps, cancelButtonProps, rootProps, screenReadOnly = false, width, maxWidth, ...restProps } = resultProps;
    useEffect(() => {
        const resizeWindow = () => {
            const dom = ref.current;
            const internalWidth = parseSizeValue(width);
            const internalMaxWidth = parseSizeValue(maxWidth);
            if (internalWidth) {
                dom.style.width = internalWidth;
                dom.style.maxWidth = 'none';
            }
            if (internalMaxWidth) {
                dom.style.maxWidth = internalMaxWidth;
            }
        };
        window.addEventListener('resize', resizeWindow);
        resizeWindow();
        return () => {
            window.removeEventListener('resize', resizeWindow);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    function afterAction() {
        afterClose?.(toastKey);
    }
    const buttonList = [
        React.createElement(ActionButton, { key: 0, type: "secondary", onClick: onCancel, afterAction: afterAction, "aria-label": t('cancel'), ...cancelButtonProps }, cancelText),
        React.createElement(ActionButton, { key: 1, onClick: onOk, afterAction: afterAction, "aria-label": t('ok'), ...okButtonProps }, okText),
    ];
    if (reverseButtons) {
        buttonList.reverse();
    }
    return (React.createElement(Box, { ...restProps, role: actionable ? 'alertdialog' : 'alert', className: clsx(toastClasses.component, className), sx: [
            (theme) => getToastComponentStyle({
                theme,
                type,
                screenReadOnly,
            }),
        ], ...rootProps, ref: ref },
        React.createElement(Box, { className: toastClasses.iconWrapper }, getIcon(props)),
        React.createElement(Box, { className: toastClasses.main },
            title && (React.createElement(Box, { className: toastClasses.title, ...titleProps }, title)),
            content && (React.createElement(Box, { className: toastClasses.content, ...contentProps }, content))),
        (closable ?? actionable) && (React.createElement(Button, { className: toastClasses.close, variant: "icon", type: "tertiary", onClick: () => {
                onClickCloseButton?.();
                afterClose?.(toastKey);
            }, "aria-label": t('close'), ...closeButtonProps },
            React.createElement(IconClose, { size: 20 }))),
        actionable && (React.createElement(Box, { className: toastClasses.action }, buttonList.map((Btn) => Btn)))));
};
